/**
 * VideoStreamContext — App-level context for the floating PiP video panel.
 *
 * This context holds the MediaStream ref and all panel state (position, size,
 * sharing status, minimised flag) so the video feed survives SPA navigation
 * between assessment pages. Individual pages opt in by calling
 * `useVideoStream().setVideoEnabled(true)` on mount and `false` on unmount;
 * the trigger button and panel only render when at least one page has opted in.
 *
 * The actual FloatingVideoPanel is rendered via a React Portal to document.body
 * so it floats above all page content without being affected by parent overflow
 * or z-index stacking contexts.
 *
 * Feature-gated: if the browser doesn't support getDisplayMedia, the context
 * silently disables itself — no errors, no UI. Safe for production.
 */

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { toast } from 'react-toastify';
import FloatingVideoPanel from '../components/FloatingVideoPanel';

const VideoStreamContext = createContext(null);

/**
 * Session-storage key for persisting panel layout between soft navigations.
 * The MediaStream itself can't be serialised — only position/size/minimised.
 */
const STORAGE_KEY = 'video-panel-layout';

/**
 * Check browser support once at module level. If getDisplayMedia is unavailable,
 * the entire feature is silently disabled (no button, no panel, no errors).
 */
const IS_SUPPORTED = typeof navigator !== 'undefined'
  && navigator.mediaDevices
  && typeof navigator.mediaDevices.getDisplayMedia === 'function';

/**
 * Read persisted layout from sessionStorage, falling back to sensible defaults
 * anchored to the bottom-right of the viewport.
 */
function readPersistedLayout() {
  try {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) return JSON.parse(cached);
  } catch { /* ignore */ }
  return {
    x: window.innerWidth - 380,
    y: window.innerHeight - 280,
    width: 360,
    height: 240,
    isMinimised: false,
  };
}

export function VideoStreamProvider({ children }) {
  // Ref-based stream storage — survives re-renders without triggering them
  const streamRef = useRef(null);
  const videoElRef = useRef(null);

  /**
   * Callback ref for the <video> element. When the element mounts (or remounts
   * after minimise→expand), we immediately attach the active stream so there's
   * no flash of a black frame. Also handles the timing edge case where
   * startCapture finishes before the video element has mounted.
   */
  const videoRef = useCallback((el) => {
    videoElRef.current = el;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
    }
  }, []);

  // How many pages have opted in. Panel hides entirely when count drops to 0.
  const enabledCountRef = useRef(0);
  const [videoEnabled, setVideoEnabledState] = useState(false);

  const [isSharing, setIsSharing] = useState(false);
  const [layout, setLayout] = useState(readPersistedLayout);

  // Persist layout to sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    } catch { /* quota exceeded — ignore */ }
  }, [layout]);

  /**
   * Pages call setVideoEnabled(true) on mount, false on unmount.
   * We reference-count so overlapping mounts (e.g. parent + child) work.
   */
  const setVideoEnabled = useCallback((enabled) => {
    if (!IS_SUPPORTED) return;
    enabledCountRef.current += enabled ? 1 : -1;
    setVideoEnabledState(enabledCountRef.current > 0);
  }, []);

  /**
   * Begin screen capture. Opens the browser's native tab/window picker.
   * If the user cancels or the browser denies permission, we silently no-op.
   *
   * Uses CaptureController (Chrome/Edge 109+) to prevent the browser from
   * auto-switching focus to the shared tab. Firefox and Safari don't exhibit
   * this behaviour so they don't need it — the feature-detect is harmless.
   */
  const startCapture = useCallback(async () => {
    if (!IS_SUPPORTED) return;
    try {
      const options = {
        video: { displaySurface: 'browser' },
        audio: false,
      };

      // CaptureController gives us control over post-capture focus behaviour.
      // Must be created before getDisplayMedia and setFocusBehavior called
      // immediately after the promise resolves (within the same microtask).
      let controller = null;
      if (typeof CaptureController !== 'undefined') { // eslint-disable-line no-undef
        controller = new CaptureController(); // eslint-disable-line no-undef
        options.controller = controller;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia(options);

      // Tell Chrome to stay on the current tab instead of switching
      if (controller) {
        try { controller.setFocusBehavior('no-focus-change'); } catch { /* unsupported — harmless */ }
      }

      streamRef.current = stream;

      // Attach the stream to the video element if it's already mounted
      if (videoElRef.current) {
        videoElRef.current.srcObject = stream;
      }

      // Listen for the user stopping the share via the browser's native bar,
      // or the shared tab being closed — both fire 'ended' on the video track.
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          handleExternalStop();
        };
      }

      setIsSharing(true);
    } catch {
      // User cancelled the picker or permission denied — no action needed
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Internal cleanup shared by both user-initiated and external stops.
   * Releases all tracks so the browser's "sharing" indicator disappears.
   */
  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoElRef.current) {
      videoElRef.current.srcObject = null;
    }
    setIsSharing(false);
  }, []);

  /** User-initiated stop (close button, Escape) — no toast needed. */
  const stopCapture = useCallback(() => {
    cleanupStream();
  }, [cleanupStream]);

  /**
   * Triggered when sharing ends externally (browser bar "Stop sharing"
   * or the shared tab is closed). Shows a brief toast so the clinician
   * knows the feed stopped without them explicitly closing it.
   */
  const handleExternalStop = useCallback(() => {
    cleanupStream();
    toast.info('Video sharing stopped', { autoClose: 3000 });
  }, [cleanupStream]);

  // Clean up stream on full unmount (e.g. logout)
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const contextValue = {
    videoEnabled,
    setVideoEnabled,
    isSharing,
    startCapture,
    stopCapture,
    videoRef,
    layout,
    setLayout,
  };

  return (
    <VideoStreamContext.Provider value={contextValue}>
      {children}
      {/* Panel is rendered via portal inside FloatingVideoPanel itself */}
      <FloatingVideoPanel />
    </VideoStreamContext.Provider>
  );
}

/**
 * Hook for pages/components to interact with the video stream.
 * Returns null if used outside the provider (shouldn't happen in practice).
 */
export function useVideoStream() {
  return useContext(VideoStreamContext);
}

export default VideoStreamContext;
